import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import RegionalService from '../services/RegionalService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import { useAuth } from '../contexts/AuthContext';
import usuarioService from '../services/UsuarioService';
import { filterAdminMenuItems, isAdminArea } from '../utils/adminMenu';

const menuItems = [
  { id: 'regional', label: 'Regional', icon: 'fas fa-map-marker-alt' },
  { id: 'profesores', label: 'Profesores', icon: 'fas fa-chalkboard-teacher' },
  // { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
  { id: 'areas', label: 'Areas', icon: 'fas fa-sitemap' },
  { id: 'admin-area', label: 'Admins de área', icon: 'fas fa-user-tie' },
  { id: 'materias', label: 'Materias', icon: 'fas fa-book' },
  { id: 'asistentes', label: 'Asistentes OpenAI', icon: 'fas fa-robot' },
  { id: 'asistentes-gpt', label: 'Asistentes GPT', icon: 'fas fa-robot' },
];

const AdminCreateAdminArea = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const stateRegionalId = location.state?.idRegional || null;
  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => stateRegionalId || RegionalService.getActiveRegionalId(), [stateRegionalId]);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional';
  const isGptClassicEnabled = useMemo(() => {
    const flags = RegionalService.getFeatureFlags();
    return Array.isArray(flags) && flags.some((flag) => {
      const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
      return nombre === 'asistentesclasicos gpt' && Boolean(flag?.activo ?? flag?.Activo);
    });
  }, []);
  const filteredMenuItems = useMemo(() => filterAdminMenuItems(menuItems, user).filter((item) => isGptClassicEnabled || isAdminArea(user) || item.id !== 'asistentes-gpt'), [user, isGptClassicEnabled]);

  const [form, setForm] = useState({
    email: '',
    dni: '',
    nombre: '',
    apellido: '',
    telefono: '',
    legajo: '',
    idRegional: activeRegionalId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    if (activeRegionalId && !form.idRegional) {
      setForm((prev) => ({ ...prev, idRegional: activeRegionalId }));
    }
  }, [activeRegionalId, form.idRegional]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      setMessage('Completa email para continuar.');
      setMessageType('error');
      return;
    }
    if (!form.dni.trim()) {
      setMessage('Completa DNI para continuar.');
      setMessageType('error');
      return;
    }
    const legajoValue = (form.legajo || '').trim();
    if (!form.idRegional) {
      setMessage('No se pudo determinar el regional activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando admin de área...');
    setMessageType('info');
    try {
      const displayName = `${form.nombre || ''} ${form.apellido || ''}`.trim() || form.email;
      const passwordToSend = form.dni.trim();

      await usuarioService.createAdminAreaUser({
        ...form,
        displayName,
        legajo: legajoValue || null,
        password: passwordToSend,
        idRegional: form.idRegional
      });
      setMessage('Admin de área creado correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'resumen' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el admin de área.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {filteredMenuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${item.id === 'admin-area' ? 'active' : ''}`}
              onClick={() => navigate('/admin-dashboard', { state: { tab: 'resumen' } })}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de admin de área</h1>
            <p className="page-subtitle">Registra un nuevo usuario administrador de área.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <div className="user-menu">
              <button className="user-menu-trigger" onClick={logout}>
                <div className="user-avatar">
                  <i className="fas fa-user-shield"></i>
                </div>
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-plus" />
                <span>Datos del admin de área</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa los datos requeridos. <strong>La contraseña inicial será el DNI</strong>; el usuario podrá cambiarla luego. El legajo es opcional.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="admin.area@ejemplo.com"
                  />
                </div>
                <div className="form-field">
                  <label>DNI</label>
                  <input
                    type="text"
                    value={form.dni}
                    onChange={(e) => handleChange('dni', e.target.value)}
                    placeholder="12345678"
                  />
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Nombre"
                    />
                  </div>
                  <div className="form-field">
                    <label>Apellido</label>
                    <input
                      type="text"
                      value={form.apellido}
                      onChange={(e) => handleChange('apellido', e.target.value)}
                      placeholder="Apellido"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={form.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="123456"
                    />
                  </div>
                  <div className="form-field">
                    <label>Legajo</label>
                    <input
                      type="text"
                      value={form.legajo}
                      onChange={(e) => handleChange('legajo', e.target.value)}
                      placeholder="C123"
                    />
                  </div>
                </div>
                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'resumen' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear admin de área</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateAdminArea;
